/**
 * Forked from https://github.com/video-dev/can-autoplay
 * MIT License 
 * Copyright (c) 2017 video-dev
 * https://github.com/video-dev/can-autoplay/blob/master/LICENSE
 */

import { INIT_DEBUGGER, TRACE } from '../../../core/debug/debugMacros';

import { action, makeObservable } from 'mobx';

import { AsyncResult, timed } from '../../../core/async';
import { StoreNode } from '../../../store';
import { Store } from '../../../store/store';
import { Error } from '../../../core/error';
import { VIDEO_DOM_EVENTS } from '../../../components/playerAdapter/playerAdapterState';
import { CanAutoplayOptions, CanAutoplayResult, DefaultCanAutoplayOptions, DefaultCanAutoplayTimeout } from './autoplaySchema';

/**
 * Utility for querying the autoplay permissions in the app.
 */
export class AutoplayManager
  extends StoreNode {

  readonly nodeType: 'AutoplayManager' = 'AutoplayManager';

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    INIT_DEBUGGER(this, {
      color: 'green'
    });

    this.init();
  }

  @action
  private async init() { /* noop */ }

  async canAutoplayVideo(opts?: CanAutoplayOptions): CanAutoplayResult {
    TRACE(this, `canAutoplayVideo()`, opts);

    const element = document.createElement('video');
    const source = await import('./autoplayTestVideo');

    TRACE(this, `Imported the test video data dynamically`);

    return this.canAutoplayMedia(element, source.AutoplayTestVideo, opts);
  }

  async canAutoplayAudio(opts?: CanAutoplayOptions): CanAutoplayResult {
    TRACE(this, `canAutoplayAudio()`, opts);

    const element = document.createElement('video');
    const source = await import('./autoplayTestAudio');

    TRACE(this, `Imported the test audio data dynamically`);

    return this.canAutoplayMedia(element, source.AutoplayTestAudio, opts);
  }

  private async canAutoplayMedia(element: HTMLAudioElement | HTMLVideoElement, source: Blob, opts?: CanAutoplayOptions): CanAutoplayResult {
    TRACE(this, `canAutoplayMedia()`);

    // unwrap the options
    opts = {
      ...DefaultCanAutoplayOptions,
      ...opts
    };

    const {
      muted,
      inline,
    } = opts;
    let { timeout } = opts;

    timeout = timeout ?? DefaultCanAutoplayTimeout;

    const handleMediaEvent = (evt: Event) => {
      if (evt.type !== 'progress' && evt.type !== 'timeupdate')
        TRACE(this, 'HTMLElement event', evt.type, evt);
    }

    // bind event listeners to the element, mostly for debugging
    VIDEO_DOM_EVENTS.forEach(evtName =>
      element.addEventListener(evtName, handleMediaEvent));

    element.muted = !!muted;
    if (muted === true)
      element.setAttribute('muted', 'muted')

    // indicates that the video is to be played "inline",
    // that is within the element's playback area.
    if (inline === true)
      element.setAttribute('playsinline', 'playsinline')

    element.src = URL.createObjectURL(source);

    const play = async (): AsyncResult<boolean, Error> => {

      try {
        TRACE(this, `Call play() on the HTMLMediaElement`);

        await element.play();

        TRACE(this, `play() call on the HTMLMediaElement succeeded, so autoplay is allowed`);
        return [true];

      } catch (rawErr) {
        TRACE(this, `play() call on the HTMLMediaElement failed, so autoplay is restricted or some other kind of error occurred`);

        const err = new Error('MediaPlayError', `Failed to play while trying to establish if autoplay is allowed.`, {
          innerError: rawErr
        });
        return [null, err];
      }
    }

    // try to play with timeout
    const result = await timed(() => play(), timeout);

    TRACE(this, `Cleaning up and returning the result`);

    // cleanup
    // unbind event listeners from the element
    VIDEO_DOM_EVENTS.forEach(evtName =>
      element.addEventListener(evtName, handleMediaEvent));

    element.src = '';
    element.srcObject = null;
    element.remove();

    return result;
  }
}
